import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  FolderOpen,
  Loader2,
  ShieldCheck,
  SlidersHorizontal,
  XCircle,
} from 'lucide-react';
import type { AppSettings, DiagnosticsInfo } from '../../global';
import { t } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';

interface SettingsViewProps {
  isDarkMode: boolean;
}

/**
 * Yönetici ayarları.
 *
 * Bu ekrana yalnızca `profiles.role = 'admin'` olan kullanıcılar ulaşır;
 * kenar çubuğunda diğer kullanıcılara hiç görünmez. Gizlemek tek başına
 * güvenlik değil — arkadaki IPC uçları da her çağrıda rolü doğruluyor
 * (bkz. services/supabase/auth.ts → assertAdmin).
 *
 * Tanılama bölümü anahtarların DEĞERİNİ değil, yalnızca tanımlı olup
 * olmadığını gösterir.
 */
export const SettingsView: React.FC<SettingsViewProps> = ({ isDarkMode }) => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [diagnostics, setDiagnostics] = useState<DiagnosticsInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [loadedSettings, loadedDiagnostics] = await Promise.all([
          window.zabit.getSettings(),
          window.zabit.getDiagnostics(),
        ]);
        if (cancelled) return;
        setSettings(loadedSettings);
        setDiagnostics(loadedDiagnostics);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const patch = async (change: Partial<AppSettings>) => {
    if (!settings) return;
    // İyimser güncelleme — anahtar/değer basit, geri alma maliyeti düşük.
    setSettings({ ...settings, ...change });
    setIsSaving(true);
    setError(null);
    try {
      const next = await window.zabit.saveSettings(change);
      setSettings(next);
      setSavedAt(Date.now());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const card = isDarkMode
    ? 'bg-[#16161A] border-[#222228]'
    : 'bg-white border-neutral-200 shadow-2xs';

  const field = isDarkMode
    ? 'bg-[#27272B] border-[#424248] text-[#F0EEE6] focus:border-[#3AC8E0]'
    : 'bg-neutral-50 border-neutral-300 text-neutral-900 focus:border-[#3AC8E0]';

  if (error && !settings) {
    return (
      <div className="max-w-3xl mx-auto w-full px-6 py-8">
        <div className="flex items-start gap-2.5 text-rose-400 text-[13px] bg-rose-500/10 border border-rose-500/25 rounded-xl p-4">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!settings || !diagnostics) {
    return (
      <div className="max-w-3xl mx-auto w-full px-6 py-8 text-neutral-500 flex items-center gap-2 text-[13px]">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('Ayarlar yükleniyor...', 'Loading settings…')}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-6 select-none">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold tracking-tight flex items-center gap-2.5 ${
            isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
          }`}>
            <SlidersHorizontal className="w-5 h-5 text-[#46CBE2]" />
            {t('Ayarlar', 'Settings')}
          </h1>
          <p className="text-[13px] text-neutral-500 mt-1.5 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            {t(
              'Yönetici bölümü — bu ekran yalnızca admin rolündeki hesaplara görünür.',
              'Admin section — only accounts with the admin role can see this screen.'
            )}
          </p>
        </div>

        {isSaving ? (
          <span className="text-[12px] text-neutral-500 flex items-center gap-1.5 shrink-0">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('Kaydediliyor', 'Saving')}
          </span>
        ) : savedAt ? (
          <span className="text-[12px] text-emerald-400 flex items-center gap-1.5 shrink-0">
            <Check className="w-3.5 h-3.5" /> {t('Kaydedildi', 'Saved')}
          </span>
        ) : null}
      </div>

      {error && (
        <div className="flex items-start gap-2.5 text-rose-400 text-[12px] bg-rose-500/10 border border-rose-500/25 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* — Transkript — */}
      <section className={`p-5 rounded-2xl border space-y-4 ${card}`}>
        <h2 className="font-bold text-[13px] text-[#F0EEE6]">{t('Transkript', 'Transcript')}</h2>

        <label className="block space-y-1.5">
          <span className="text-[12px] font-medium text-neutral-400">{t('Konuşma dili', 'Spoken language')}</span>
          <select
            value={settings.transcribeLanguage}
            onChange={(e) => void patch({ transcribeLanguage: e.target.value as AppSettings['transcribeLanguage'] })}
            className={`w-full px-3 py-2 rounded-xl border text-[13px] outline-none transition-colors ${field}`}
          >
            <option value="tr">{t('Türkçe', 'Turkish')}</option>
            <option value="en">{t('İngilizce', 'English')}</option>
            <option value="auto">{t('Otomatik algıla', 'Detect automatically')}</option>
          </select>
          <span className="text-[11px] text-neutral-500 block">
            {t(
              'Canlı kayıtlar için geçerli; yüklenen dosyaların dili kendiliğinden algılanır. Otomatik seçilirse Deepgram çok dilli modda çalışır — Türkçe toplantılarda "Türkçe" daha isabetli.',
              'Applies to live recordings; the language of uploaded files is detected automatically. With automatic detection Deepgram runs in multilingual mode — picking the actual language is more accurate.'
            )}
          </span>
        </label>
      </section>

      {/* — Takvim — */}
      <section className={`p-5 rounded-2xl border space-y-4 ${card}`}>
        <h2 className="font-bold text-[13px] text-[#F0EEE6]">{t('Takvim', 'Calendar')}</h2>
        <label className="block space-y-1.5">
          <span className="text-[12px] font-medium text-neutral-400">
            {t('Kaç gün ileriye bakılsın:', 'How many days ahead to look:')}{' '}
            <span className="font-mono text-[#46CBE2]">
              {t(`${settings.calendarLookaheadDays} gün`, `${settings.calendarLookaheadDays} days`)}
            </span>
          </span>
          <input
            type="range"
            min={1}
            max={30}
            step={1}
            value={settings.calendarLookaheadDays}
            onChange={(e) => void patch({ calendarLookaheadDays: Number(e.target.value) })}
            className="w-full h-1.5 bg-neutral-700/50 rounded-lg appearance-none cursor-pointer accent-[#3AC8E0]"
          />
        </label>
      </section>

      {/* — Tanılama — */}
      <section className={`p-5 rounded-2xl border space-y-4 ${card}`}>
        <div>
          <h2 className="font-bold text-[13px] text-[#F0EEE6]">{t('Yapılandırma Tanılaması', 'Configuration diagnostics')}</h2>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            {t(
              'Anahtarların değerleri gösterilmez — yalnızca tanımlı olup olmadıkları.',
              'Key values are never shown — only whether they are set.'
            )}
          </p>
        </div>

        <div className="space-y-1.5">
          {diagnostics.keys.map((key) => (
            <div key={key.name} className="flex items-center justify-between text-[12px] py-1">
              <span className="font-mono text-neutral-400">
                {key.name}
                {!key.required && <span className="text-neutral-600 ml-1.5">{t('(isteğe bağlı)', '(optional)')}</span>}
              </span>
              {key.present ? (
                <span className="text-emerald-400 flex items-center gap-1.5 shrink-0">
                  <Check className="w-3.5 h-3.5" /> {t('tanımlı', 'set')}
                </span>
              ) : (
                <span className={`flex items-center gap-1.5 shrink-0 ${key.required ? 'text-rose-400' : 'text-neutral-500'}`}>
                  <XCircle className="w-3.5 h-3.5" /> {t('eksik', 'missing')}
                </span>
              )}
            </div>
          ))}
        </div>

        <dl className="text-[11.5px] space-y-1.5 border-t border-neutral-700/30 pt-3.5">
          {[
            [t('Uygulama', 'App'), `${diagnostics.appVersion} · Electron ${diagnostics.electronVersion}`],
            ['Platform', diagnostics.platform],
            [t('.env kaynağı', '.env source'), diagnostics.envSource ?? t('bulunamadı (süreç ortamı)', 'not found (process environment)')],
            [t('Uygulama verisi', 'App data'), diagnostics.userDataPath],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-3">
              <dt className="text-neutral-500 shrink-0 w-28">{label}</dt>
              <dd className="text-neutral-300 font-mono break-all">{value}</dd>
            </div>
          ))}

          <div className="flex gap-3 items-start">
            <dt className="text-neutral-500 shrink-0 w-28">{t('Kayıt klasörü', 'Recordings folder')}</dt>
            <dd className="text-neutral-300 font-mono break-all flex-1">
              {diagnostics.recordingsPath}
              {!IS_WEB && (
                <button
                  onClick={() => void window.zabit.revealRecording(diagnostics.recordingsPath).catch(() => {})}
                  title={t('Klasörü aç', 'Open folder')}
                  className="ml-2 inline-flex align-middle text-neutral-500 hover:text-[#46CBE2] transition-colors"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                </button>
              )}
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
};
